import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { strCode } from "../../../../../consts";
import { t } from "../../../../../services";
import { appsUtil } from "../../../../../utils";

import {
  AddButton,
  DiscardModal,
  FlexContainers,
  Modal,
  Paragraph2,
  WrapperContainer
} from "../../../../../components";
import { FormSection } from "../../../../../templates";
import { RoleCard } from "./RoleCard";
import { RoleForm } from "./RoleForm";
import { RolesListContainer } from "./styled";

const RolesList = ({ values = [], roleDuplicateErrors = [], setValues = () => {} }) => {
  const [selectedRoleConfig, setSelectedRoleConfig] = useState(null);
  const [isPrepopulated, setIsPrepopulated] = useState(true);
  const [configToDelete, setConfigToDelete] = useState(null);

  const selectedRolesList = useMemo(() => {
    return values.map((config) => {
      return { application: config?.application?.id, role: config?.role?.id };
    });
  }, [values]);

  const isAppAvailable = useMemo(() => {
    if (selectedRoleConfig) {
      const application = selectedRoleConfig?.application?.id;
      const isAppAvailable = application
        ? appsUtil.isAppAvailable(application) && appsUtil.isAppActive(application)
        : true;

      return isAppAvailable;
    }

    return true;
  }, [selectedRoleConfig]);

  const configErrorIds = useMemo(() => {
    const errorIds = [];
    roleDuplicateErrors.forEach((error) => {
      errorIds.push(...error.configIds);
    });

    return errorIds;
  }, [roleDuplicateErrors]);

  useEffect(() => {
    if (isPrepopulated && values.length) {
      setIsPrepopulated(false);
    }

    if (!values.length) {
      setValues([]);
    }
  }, [values.length]);

  const onDeleteRoleConfig = () => {
    const newValues = values.filter((config) => config.id !== configToDelete?.id);
    setValues(newValues);
    setConfigToDelete(null);
  };

  const onEditRoleConfig = (config) => {
    setSelectedRoleConfig(config);
  };

  const onClose = () => {
    setSelectedRoleConfig(null);
  };

  const onSaveRole = (roleData) => {
    const configIndex = values.findIndex((item) => item.id === roleData.id);

    if (configIndex !== -1) {
      const newRolesList = [
        ...values.slice(0, configIndex),
        roleData,
        ...values.slice(configIndex + 1)
      ];
      setValues(newRolesList);
    } else {
      const lastItemId = values[values.length - 1]?.id;
      const newId = lastItemId != undefined ? lastItemId + 1 : 0;
      const newRoleConfig = { ...roleData, id: newId };

      setValues((prev) => [...prev, newRoleConfig]);
    }
  };

  return (
    <FormSection
      title={t(strCode.ROLES_TITLE)}
      Component={
        <WrapperContainer Container={FlexContainers.FlexRowEnd} width={"100%"}>
          <AddButton onClick={() => setSelectedRoleConfig({})}>{t(strCode.ADD_ROLE_BTN)}</AddButton>
        </WrapperContainer>
      }>
      <RolesListContainer hasRoles={!!values.length}>
        {values.length ? (
          values.map((roleConfig) => {
            return (
              <RoleCard
                key={roleConfig.id}
                roleConfig={roleConfig}
                hasError={configErrorIds.includes(roleConfig.id)}
                onDeleteConfig={setConfigToDelete}
                onEditConfig={onEditRoleConfig}
              />
            );
          })
        ) : (
          <Paragraph2>{t(strCode.NO_USER_ROLES_INFO)}</Paragraph2>
        )}
      </RolesListContainer>

      <Modal
        id={"role-form-modal"}
        active={selectedRoleConfig != null}
        setActive={onClose}
        closable={false}
        title={t(strCode.ADD_ROLE_BTN)}>
        <RoleForm
          roleConfig={selectedRoleConfig}
          selectedRolesList={selectedRolesList}
          isPrepopulated={isPrepopulated}
          hasError={configErrorIds.includes(selectedRoleConfig?.id)}
          disabled={!isAppAvailable}
          onClose={onClose}
          onSaveRole={onSaveRole}
        />
      </Modal>

      <DiscardModal
        title={strCode.DELETE_ROLE_TITLE}
        text={t(strCode.DELETE_ROLE_TEXT, { role: configToDelete?.role?.value })}
        active={!!configToDelete}
        confirmBtnText={strCode.DELETE_BTN}
        setActive={() => {
          setConfigToDelete(null);
        }}
        onDiscard={onDeleteRoleConfig}
      />
    </FormSection>
  );
};

RolesList.propTypes = {
  values: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      application: PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      }),
      market: PropTypes.arrayOf(
        PropTypes.shape({ id: PropTypes.string.isRequired, value: PropTypes.string.isRequired })
      ).isRequired,
      location: PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      }),
      role: PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      }),
      from: PropTypes.instanceOf(Date),
      to: PropTypes.instanceOf(Date)
    }).isRequired
  ),
  roleDuplicateErrors: PropTypes.arrayOf(
    PropTypes.shape({
      application: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      configIds: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
      ).isRequired
    })
  ).isRequired,
  setValues: PropTypes.func.isRequired
};

export default RolesList;
